import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FileText, Loader2, ExternalLink } from 'lucide-react'
import { kontakteAPI, type AkteInfo } from '@/utils/api'

interface AktenTabProps {
  kontaktId: number
}

export function AktenTab({ kontaktId }: AktenTabProps) {
  const navigate = useNavigate()
  const [akten, setAkten] = useState<AkteInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAkten = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const data = await kontakteAPI.getKontaktAkten(kontaktId)
        setAkten(data)
      } catch (err) {
        console.error('Failed to fetch akten:', err)
        setError('Akten konnten nicht geladen werden.')
      } finally {
        setIsLoading(false)
      }
    }
    fetchAkten()
  }, [kontaktId])

  const handleAkteClick = (akteId: number) => {
    navigate({ to: '/lawyer/cases/$caseId', params: { caseId: String(akteId) } })
  }

  // Format date nicely
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // Get state badge color
  const getStateBadgeColor = (state: string) => {
    switch (state) {
      case 'draft':
        return 'bg-slate-100 text-slate-700'
      case 'submitted':
        return 'bg-blue-100 text-blue-700'
      case 'in_progress':
        return 'bg-amber-100 text-amber-700'
      case 'resolved':
        return 'bg-green-100 text-green-700'
      case 'closed':
        return 'bg-purple-100 text-purple-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  // Get incident type label
  const getIncidentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      delay: 'Verspätung',
      cancellation: 'Annullierung',
      denied_boarding: 'Nichtbeförderung',
      downgrade: 'Herabstufung',
      other: 'Sonstiges',
    }
    return types[type] || type
  }

  // Get role badge color
  const getRoleBadgeColor = (rolle: string) => {
    switch (rolle) {
      case 'mandant':
        return 'bg-blue-100 text-blue-700'
      case 'beklagter':
        return 'bg-red-100 text-red-700'
      case 'rsv':
        return 'bg-green-100 text-green-700'
      case 'gericht':
        return 'bg-purple-100 text-purple-700'
      case 'zeuge':
        return 'bg-amber-100 text-amber-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex flex-col items-center justify-center h-32">
          <Loader2 className="h-6 w-6 text-slate-400 animate-spin mb-2" />
          <p className="text-sm text-slate-500">Akten werden geladen...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5">
        <div className="flex flex-col items-center justify-center h-32">
          <FileText className="h-8 w-8 text-red-300 mb-2" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-slate-400" />
        Verknüpfte Akten
        {akten.length > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
            {akten.length}
          </span>
        )}
      </h2>

      {akten.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <FileText className="h-12 w-12 text-slate-200 mb-3" />
          <p className="text-slate-500 text-center">
            Dieser Kontakt ist in keiner Akte verknüpft.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                  Aktenzeichen
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                  Rolle
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                  Vorfall
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                  Erstellt
                </th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {akten.map((akte) => (
                <tr
                  key={`${akte.akte_id}-${akte.rolle}`}
                  onClick={() => handleAkteClick(akte.akte_id)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <span className="font-mono font-medium text-slate-900">
                      {akte.aktenzeichen}
                    </span>
                    {akte.is_primary && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
                        Primär
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${getRoleBadgeColor(
                        akte.rolle
                      )}`}
                    >
                      {akte.rolle_display}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {getIncidentTypeLabel(akte.incident_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${getStateBadgeColor(
                        akte.state
                      )}`}
                    >
                      {akte.state}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {formatDate(akte.created_at)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <ExternalLink className="h-4 w-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
